/**
 * PasswordChangeModal.jsx
 * 비밀번호 변경 모달 컴포넌트
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

export default function PasswordChangeModal({ isOpen, onClose }) {
    const [form, setForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 유효성 검사
        if (form.newPassword.length < 8) {
            setMessage({ type: 'error', text: '새 비밀번호는 8자 이상이어야 합니다.' });
            return;
        }

        if (form.newPassword !== form.confirmPassword) {
            setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
            return;
        }

        setIsLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const result = await api.changePassword(
                form.currentPassword,
                form.newPassword,
                form.confirmPassword
            );

            if (result.error) {
                setMessage({ type: 'error', text: result.error });
            } else {
                setMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
                // 폼 초기화
                setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                // 2초 후 모달 닫기
                setTimeout(() => {
                    onClose();
                    setMessage({ type: '', text: '' });
                }, 2000);
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message || '비밀번호 변경에 실패했습니다.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setMessage({ type: '', text: '' });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                onClick={(e) => e.target === e.currentTarget && handleClose()}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-[#1e293b] border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                >
                    {/* 헤더 */}
                    <div className="px-6 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-cyan-900/50 flex items-center justify-center text-cyan-400">
                                <i className="fas fa-key"></i>
                            </div>
                            <h3 className="text-lg font-bold text-white">비밀번호 변경</h3>
                        </div>
                        <button onClick={handleClose} className="text-slate-400 hover:text-white transition-colors">
                            <i className="fas fa-times text-lg"></i>
                        </button>
                    </div>

                    {/* 폼 */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* 알림 메시지 */}
                        {message.text && (
                            <div className={`p-3 rounded-lg text-sm ${message.type === 'error'
                                    ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                                    : 'bg-green-500/10 border border-green-500/30 text-green-300'
                                }`}>
                                {message.text}
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">
                                <i className="fas fa-lock mr-1"></i>현재 비밀번호
                            </label>
                            <input
                                type="password"
                                value={form.currentPassword}
                                onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
                                className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                                placeholder="현재 비밀번호 입력"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">
                                <i className="fas fa-key mr-1"></i>새 비밀번호
                            </label>
                            <input
                                type="password"
                                value={form.newPassword}
                                onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
                                className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                                placeholder="새 비밀번호 (8자 이상)"
                                minLength={8}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">
                                <i className="fas fa-check-double mr-1"></i>새 비밀번호 확인
                            </label>
                            <input
                                type="password"
                                value={form.confirmPassword}
                                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                                className="w-full px-4 py-3 rounded-lg bg-slate-800/60 border border-slate-700/30 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                                placeholder="새 비밀번호 다시 입력"
                                minLength={8}
                                required
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="flex-1 py-3 rounded-lg font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 transition-colors"
                            >
                                취소
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex-1 py-3 rounded-lg font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <><i className="fas fa-spinner fa-spin mr-2"></i>처리 중...</>
                                ) : (
                                    <><i className="fas fa-check mr-2"></i>변경하기</>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
